import React from 'react';
import {
  Box,
  Typography,
  Badge,
  Collapse,
  makeStyles,
} from '@material-ui/core';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const useStyles = makeStyles({
  navItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 8px',
    cursor: 'pointer',
    color: '#ffffff',
    transition: 'all 0.2s',
    textDecoration: 'none',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
  },
  navItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    width: '100%',
  },
  navItemActive: {
    color: '#60a5fa',
    backgroundColor: 'rgba(96,165,250,0.1)',
  },
  navIcon: {
    fontSize: '22px',
    marginBottom: '2px',
  },
  navText: {
    fontSize: '10px',
    textAlign: 'center',
    fontWeight: 500,
    lineHeight: 1.2,
    width: '100%',
  },
  expandIcon: {
    fontSize: '14px',
  },
  subItem: {
    padding: '8px 4px',
    margin: '2px 8px',
    borderRadius: '6px',
    position: 'relative',
  },
  subItemActive: {
    backgroundColor: 'rgba(96,165,250,0.08)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-8px',
      top: '50%',
      width: '8px',
      height: '16px',
      borderLeft: '2px solid rgba(96,165,250,0.4)',
      borderBottom: '2px solid rgba(96,165,250,0.4)',
      borderBottomLeftRadius: '6px',
      transform: 'translateY(-100%)',
    },
  },
});

interface NavItemProps {
  icon: React.ComponentType<any>;
  text: string;
  to?: string;
  badge?: number;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
  isSubItem?: boolean;
}

export const NavItem = ({
  icon: Icon,
  text,
  to,
  badge,
  expandable,
  isExpanded,
  onToggle,
  children,
  isSubItem,
}: NavItemProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = to && location.pathname.startsWith(`/${to}`);

  const handleClick = () => {
    if (expandable && onToggle) {
      onToggle();
    } else if (to) {
      navigate(`/${to}`);
    }
  };

  return (
    <>
      <Box
        className={`${classes.navItem} ${
          isActive ? classes.navItemActive : ''
        } ${isSubItem ? classes.subItem : ''} ${
          isSubItem && isActive ? classes.subItemActive : ''
        }`}
        onClick={handleClick}
      >
        <Box className={classes.navItemContent}>
          {badge ? (
            <Badge badgeContent={badge} color="error">
              <Icon
                className={classes.navIcon}
                style={isSubItem ? { fontSize: '18px', opacity: 0.85 } : {}}
              />
            </Badge>
          ) : (
            <Icon
              className={classes.navIcon}
              style={isSubItem ? { fontSize: '18px', opacity: 0.85 } : {}}
            />
          )}
          {expandable &&
            (isExpanded ? (
              <ExpandLessIcon className={classes.expandIcon} />
            ) : (
              <ExpandMoreIcon className={classes.expandIcon} />
            ))}
        </Box>
        <Typography
          className={classes.navText}
          style={isSubItem ? { fontSize: '8px', fontWeight: 400 } : {}}
        >
          {text}
        </Typography>
      </Box>
      {expandable && (
        <Collapse in={isExpanded}>
          <Box sx={{ position: 'relative' }}>{children}</Box>
        </Collapse>
      )}
    </>
  );
};
